import { FC, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Dropdown, Space } from 'antd';
import { TbCopy, TbDotsVertical, TbEdit, TbTrash, TbWebhook, TbWorldShare } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DropdownMenuPanel } from '@/shared';
import RenameResourceModal from '@/shared/components/modals/RenameResourceModal';
import { DeletePrimitiveModal } from '@/shared/components/modals/ResourceActionModals';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { Webhook } from '@/types';
import { formatDate } from '@/utilities';

import DeveloperCredentialListItem from '../../../components/DeveloperCredentialListItem';
import DeveloperCredentialPreview from '../../../components/DeveloperCredentialPreview';

export type WebhookListRecord = Webhook & { signingSecretPreview?: string };

interface WebhookListItemProps {
    item: WebhookListRecord;
    onDeleted?: (webhookId: string) => void;
    onUpdated?: (webhook: WebhookListRecord) => void;
}

const WebhookListItem: FC<WebhookListItemProps> = ({ item, onDeleted, onUpdated }) => {
    const { token } = useAntDesignTheme();
    const [busy, setBusy] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [renameOpen, setRenameOpen] = useState(false);
    const maskedSecret = item.signingSecretPreview || item.signingSecret || 'Signing secret';

    const openPortal = async () => {
        try {
            setBusy(true);
            const { data } = await authorizedAxiosInstance.post<{ url: string }>(
                `/api/webhooks/${item.id}/app-portal-access`,
            );
            window.open(data.url, '_blank', 'noopener,noreferrer');
        } catch (error) {
            captureException(error);
            toast.error('Unable to open webhook portal');
        } finally {
            setBusy(false);
        }
    };

    const deleteWebhook = async () => {
        try {
            await authorizedAxiosInstance.delete(`/api/webhooks/${item.id}`);
            onDeleted?.(item.id);
            toast('Webhook Deleted', { description: item.name });
        } catch (error) {
            captureException(error);
            toast.error('Unable to delete webhook');
            throw error;
        }
    };

    return (
        <>
            <DeveloperCredentialListItem
                itemKey={item.id}
                extra={
                    <Space.Compact block>
                        <DeveloperCredentialPreview value={maskedSecret} />
                        <Button
                            aria-label='Open webhook portal'
                            title='Open webhook portal'
                            size='small'
                            disabled={busy}
                            icon={<TbWorldShare />}
                            onClick={() => void openPortal()}
                        />
                        <Dropdown
                            trigger={['click']}
                            placement='bottomRight'
                            popupRender={() => (
                                <DropdownMenuPanel
                                    {...{
                                        items: [
                                            {
                                                key: 'copy-endpoint',
                                                icon: <TbCopy size='1rem' />,
                                                label: 'Copy Endpoint',
                                            },
                                            { key: 'rename', icon: <TbEdit size='1rem' />, label: 'Rename' },
                                            { type: 'divider' },
                                            {
                                                key: 'delete',
                                                danger: true,
                                                icon: <TbTrash size='1rem' />,
                                                label: 'Delete',
                                            },
                                        ],
                                        onClick: ({ key }) => {
                                            if (key === 'copy-endpoint') {
                                                void navigator.clipboard.writeText(item.endpoint);
                                                toast('Webhook Endpoint Copied');
                                            }
                                            if (key === 'rename') {
                                                setRenameOpen(true);
                                            }
                                            if (key === 'delete') {
                                                setDeleteOpen(true);
                                            }
                                        },
                                    }}
                                />
                            )}
                        >
                            <Button
                                aria-label='Webhook actions'
                                size='small'
                                disabled={busy}
                                icon={<TbDotsVertical style={{ color: token.colorTextTertiary }} />}
                            />
                        </Dropdown>
                    </Space.Compact>
                }
                avatarIcon={<TbWebhook size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                name={item.name}
                status='Active'
                description={item.createdAt ? `Created ${formatDate(item.createdAt)}` : undefined}
            />
            <RenameResourceModal
                open={renameOpen}
                currentName={item.name}
                resourceLabel='Webhook'
                onCancel={() => setRenameOpen(false)}
                onRename={async (name) => {
                    const { data } = await authorizedAxiosInstance.put<WebhookListRecord>(`/api/webhooks/${item.id}`, {
                        name,
                    });
                    onUpdated?.(data);
                    setRenameOpen(false);
                    toast('Webhook Renamed', { description: name });
                }}
            />
            <DeletePrimitiveModal
                open={deleteOpen}
                resourceLabel='Webhook'
                resourceName={item.name}
                warning='Deleting this webhook permanently stops event delivery to its configured endpoint.'
                onCancel={() => setDeleteOpen(false)}
                onDelete={deleteWebhook}
            />
        </>
    );
};

export default WebhookListItem;
